import React, { useCallback, useRef, useState } from "react";
import { MapPin, RotateCcw } from "lucide-react";
import { TspCard, TspEmptyState } from "../../../components/primitives";
import TspMap from "../../../components/map/TspMap";
import useTspStateBoundaries from "../../journeys/useTspStateBoundaries";
import { boundsOf, featuresBounds } from "../../journeys/coverageMapModel";
import { TSP_MAP_CONFIG } from "../../../constants/tspMapConfig";
import { formatNumber } from "../../../utils/tspFormat";

const plural = (count, word) => `${formatNumber(count)} ${word}${count === 1 ? "" : "s"}`;

// A cluster counts the JOURNEY PLACES it holds (a marker can hold several at
// one spot), so the numbers on the map add up to places, not just dots.
function describeCluster(cluster) {
  const places = cluster.points.reduce((sum, point) => sum + ((point.places || []).length || 1), 0);
  const journeys = new Set(cluster.points.flatMap((point) => point.journey_ids || [])).size;
  return {
    count: places,
    lines: [`${plural(places, "journey place")} · ${plural(journeys, "journey")}`, "Select to zoom in"],
    label: `${plural(places, "journey place")} close together — zoom in`,
  };
}

// India map of every journey place the caller can see (`locations` from
// buildJourneyLocations; null without the journeys + city visits permissions):
// each place of a journey is its own marker, at its saved coordinates (its
// visit's GPS, else its planned point); nearby markers merge into numbered
// clusters, and a click on a marker shows that place (or every place at that spot).
export default function JourneyLocationsMap({ locations }) {
  const geo = useTspStateBoundaries();
  const [focus, setFocus] = useState(null);
  const focusCount = useRef(0);

  const moveTo = useCallback((bounds) => {
    focusCount.current += 1;
    setFocus(bounds ? { key: `zoom:${focusCount.current}`, bounds, maxZoom: TSP_MAP_CONFIG.cityZoom } : { key: `india:${focusCount.current}` });
  }, []);
  const onClusterClick = useCallback((cluster) => moveTo(boundsOf(cluster.points)), [moveTo]);
  const onStateClick = useCallback(
    (feature) => {
      const bounds = featuresBounds(geo.states, [feature.properties.lgd_code]);
      if (bounds) moveTo(bounds);
    },
    [geo.states, moveTo]
  );

  if (!locations) {
    return (
      <TspCard title="Journey Locations" className="tsp-dashboard-map-card">
        <p className="tsp-muted-text">You do not have access to journey locations.</p>
      </TspCard>
    );
  }

  const { points, coverage, journeyCount, locatedJourneyCount, placeCount } = locations;
  const unlocated = journeyCount - locatedJourneyCount;

  return (
    <TspCard
      title="Journey Locations"
      subtitle={`${plural(locatedJourneyCount, "journey")} · ${plural(placeCount, "place")}`}
      className="tsp-dashboard-map-card"
      action={
        focus && (
          <button type="button" className="tsp-download-text-btn" onClick={() => moveTo(null)}>
            <RotateCcw size={14} /> All India
          </button>
        )
      }
    >
      <div className="tsp-dashboard-map">
        <TspMap
          points={points}
          coverage={coverage}
          boundaries={geo.states}
          nationalBoundary={geo.national}
          onClusterClick={onClusterClick}
          onStateClick={onStateClick}
          describeCluster={describeCluster}
          focus={focus}
          ariaLabel="India map of journey locations"
          onResetView={() => moveTo(null)}
        />
        {points.length === 0 && (
          <div className="tsp-dashboard-map-empty">
            <TspEmptyState icon={MapPin} title="No journey locations yet" message="Journey places appear here once they have a saved location." />
          </div>
        )}
      </div>
      {unlocated > 0 && (
        <p className="tsp-muted-text tsp-dashboard-map-note">
          {plural(unlocated, "journey")} {unlocated === 1 ? "has" : "have"} no saved location yet and {unlocated === 1 ? "is" : "are"} not on the map.
        </p>
      )}
    </TspCard>
  );
}
